import { inject, Injectable } from '@angular/core';
import { IRequestSearch } from '../models/search-request.model';
import { filter, Observable, of, switchMap } from 'rxjs';
import { IRequestSearchResult } from '../models/search-result.model';
import { IRequest } from '../models/request.model';
import { IApprovalRoute } from '../models/approval-route.model';
import { IRequestHistory } from '../models/request-history.model';
import { IRequestType } from '../models/request-type.model';
import { manageRequestConst } from '../manage-request.const';
import { BaseHttpService } from '../../services/common/base.http.service';
import { IDivision } from '../models/division.model';
import { SearchParameters } from '../../shared/models/searchParameters.model';
import { PagedResult } from '../../shared/models/pagedResult.model';
import { HttpHeaders } from '@angular/common/http';
import { IRequestForward } from '../models/request-forward.model';
import { LoggedInUserService } from '../../services/common/logged-in-user.service';
import { IRequestTerminate } from '../models/request-terminate.model';
import { IRequestReady } from '../models/request-ready.model';
import { IRequestReturn } from '../models/request-return.model';
import { IRequestApprove } from '../models/request-approve.model';
import { IRequestSendToJde } from '../models/request-send-to-jde.model';
import { ResultInfo } from '../../shared/models/result-info.model';
import { BlobModel } from '../models/blob.model';

@Injectable({
  providedIn: 'root'
})
export class SearchRequestService {

  baseHttpService = inject(BaseHttpService);
  loggedInUserService = inject(LoggedInUserService);
  userEmail: string;

  searchRequest(searchParams: SearchParameters<IRequestSearch>): Observable<PagedResult<IRequestSearchResult[]>> {
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    return this.loggedInUserService.currentUser$
      .pipe(
        filter(data => data !== null),
        switchMap(data => {
          searchParams.userEmail = data.userEmail;
          return this.baseHttpService.post<PagedResult<IRequestSearchResult[]>>(manageRequestConst.searchRequestApiUrl, searchParams, { headers: headers });
        })
      );
  }

  getAttachments(id: string): Observable<BlobModel[]> {
    return this.baseHttpService.get<BlobModel[]>(manageRequestConst.attachments + id );
  }

  forwardRequest(params: IRequestForward): Observable<IRequest> {
      const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
      return this.baseHttpService.post<IRequest>(manageRequestConst.requestForward, params, { headers: headers });
  }

  cancelRequest(params: IRequestTerminate): Observable<any> {
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    return this.baseHttpService.post(manageRequestConst.requestCancel, params, { headers: headers });
  }

  closeRequest(params: IRequestTerminate): Observable<any> {
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    return this.baseHttpService.post(manageRequestConst.requestClose, params, { headers: headers });
  }

  requestReady(params: IRequestReady): Observable<any> {
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    return this.baseHttpService.post(manageRequestConst.requestReady, params, { headers: headers });
  }

  returnRequest(params: IRequestReturn): Observable<any> {
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    return this.baseHttpService.post(manageRequestConst.returnRequest, params, { headers: headers });
  }

  approveRequest(params: IRequestApprove): Observable<ResultInfo> {
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    return this.baseHttpService.post(manageRequestConst.requestApprove, params, { headers: headers });
  }

  requestSendToJde(params: IRequestSendToJde): Observable<any> {
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    return this.baseHttpService.post(manageRequestConst.requestSendToJde, params, { headers: headers });
  }

  getRequestDetails(requestId: string): Observable<IRequest> {
    return this.baseHttpService.get<IRequest>(manageRequestConst.requestDetails + requestId);
  }

  loadApprovalRouteData(requestNumber: number, action?: string): Observable<IApprovalRoute[]> {
    return this.baseHttpService.get<IApprovalRoute[]>(`/request/${requestNumber}/approvers` + (action ? `?action=${action}` : ''));
  }

  loadRequestHistoryData(requestNumber: number): Observable<IRequestHistory[]> {
    return this.baseHttpService.get<IRequestHistory[]>(manageRequestConst.requestHistory + requestNumber);
  }

  public getAllRequestTypes(): Observable<IRequestType[]> {    
    return this.baseHttpService.get<IRequestType[]>(manageRequestConst.requestTypesApiUrl);
  }

  public getAllDivisions(): Observable<IDivision[]> {
    return this.baseHttpService.get<IDivision[]>(manageRequestConst.divisionsApiUrl);
  }
}